import React, { FC } from 'react';
import moment from 'moment';
import Datetime, { ViewMode } from 'react-datetime';
import TextInput from '../text/TextInput';

export interface Props {
  id?: string;
  enabled: boolean;
  placeholder: string | undefined;
  value: string | undefined;
  touched?: boolean;
  error?: string;
  mode: 'time' | 'datetime' | 'date' | 'birthdate';
  onClose?: () => void;
  onChange: (value: string) => void;
  readOnlyMode?: boolean;
  style?: React.CSSProperties;
}

const DateTime: FC<Props> = ({
  id,
  mode,
  enabled,
  placeholder,
  value,
  touched,
  error,
  onClose,
  onChange,
  readOnlyMode,
  style,
}: Props) => {
  let initialViewMode: ViewMode = 'days';
  let dateFormat: string | boolean = true;
  let timeFormat: string | boolean = false;
  let initialViewDate: moment.Moment | undefined = undefined;
  let valueFormat = 'L h:mm A';
  let onOpen: (() => void) | undefined = undefined;
  let icon = 'mdi mdi-clock';
  const datetimeWorkaround = (value) => (value === '' || value === null ? { value: '' } : {});

  switch (mode) {
    case 'time':
      initialViewMode = 'time';
      initialViewDate = undefined;
      dateFormat = false;
      timeFormat = 'h:mm A';
      valueFormat = 'h:mm A';
      icon = 'mdi mdi-clock';
      onOpen = () => {
        if (!value) {
          onChange(moment().format('h:mm A'));
        }
      };
      break;
    case 'date':
      initialViewMode = 'days';
      initialViewDate = undefined;
      dateFormat = 'YYYY-MM-DD';
      timeFormat = false;
      valueFormat = 'YYYY-MM-DD';
      onOpen = () => {
        if (!value) {
          onChange(moment().format('YYYY-MM-DD'));
        }
      };
      icon = 'mdi mdi-calendar';
      break;
    case 'datetime':
      initialViewMode = 'days';
      initialViewDate = undefined;
      dateFormat = 'YYYY-MM-DD';
      timeFormat = 'h:mm A';
      valueFormat = 'YYYY-MM-DD h:mm A';
      onOpen = () => {
        if (!value) {
          onChange(moment('1990-01-01 12:00').format('YYYY-MM-DD h:mm A'));
        }
      };
      icon = 'mdi mdi-calendar-clock';
      break;
    case 'birthdate':
      initialViewMode = 'years';
      initialViewDate = moment('1990-01-01', 'YYYY-MM-DD');
      dateFormat = 'YYYY-MM-DD';
      timeFormat = false;
      valueFormat = 'YYYY-MM-DD';
      onOpen = () => {
        if (!value) {
          onChange(moment('1990-01-01').format('YYYY-MM-DD'));
        }
      };
      icon = 'mdi mdi-cake-variant';
      break;
  }

  return (
    <Datetime
      initialViewMode={initialViewMode}
      closeOnSelect
      value={value}
      initialViewDate={initialViewDate}
      timeFormat={timeFormat}
      dateFormat={dateFormat}
      onChange={async (e) => {
        if (typeof e !== 'string') {
          await onChange(e.format(valueFormat));
          if (onClose) {
            onClose();
          }
        }
      }}
      inputProps={{
        ...datetimeWorkaround(value),
      }}
      onClose={onClose}
      onOpen={onOpen}
      renderInput={(propsCal) => {
        let onReset;

        if (!readOnlyMode) {
          onReset = () => {
            propsCal.onChange({ target: { value: '' } });
            onChange('');
          };
        }

        return (
          <div className="flex-1">
            <TextInput
              id={id}
              value={propsCal.value}
              touched={touched}
              error={error}
              enabled={enabled}
              placeholder={placeholder}
              onChange={(e) => {
                propsCal.onChange(e);
                onChange(e.target.value);
              }}
              onReset={onReset}
              rest={{
                onClick: propsCal.onClick,
                onFocus: propsCal.onFocus,
                onKeyDown: propsCal.onKeyDown,
                autoComplete: 'off',
              }}
              style={{
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                height: 'auto',
                ...style,
              }}
              readOnlyMode={readOnlyMode}
              iconComponent={<span className={icon} />}
            />
          </div>
        );
      }}
    />
  );
};

export default DateTime;
